
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Box,
  Badge,
  IconButton,
  Tooltip,
  Text,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { Edit2, Trash2, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';
import { Metric, MetricGoal, User } from '../../types';
import { getQuarterWeeks, formatMetricValue, isMetricOnTrack, calculateTrailingStatus } from '../../utils/scorecardCalculations';

interface ScorecardTableViewProps {
  metrics: Metric[];
  metricGoals: MetricGoal[];
  users: User[];
  quarter: number;
  year: number;
  onEdit: (metric: Metric) => void;
  onDelete: (metric: Metric) => void;
}

export function ScorecardTableView({
  metrics,
  metricGoals,
  users,
  quarter,
  year,
  onEdit,
  onDelete,
}: ScorecardTableViewProps) {
  // Current week calculation
  const currentWeekNumber = (() => {
    // This logic mimics getQuarterWeeks but finds the current week index
    const weeks = getQuarterWeeks(quarter, year);
    const currentWeek = weeks.find((w) => w.isReviewWeek);
    // If we are in the quarter, return that week number. 
    // If future, maybe show first week? If past, last week?
    // For now, default to last week if quarter is past, or first if future, or current.
    if (currentWeek) return currentWeek.weekNumber;
    return weeks[weeks.length - 1].weekNumber; 
  })();

  // Calculate weeks once
  const weeks = getQuarterWeeks(quarter, year);

  const getMetricStatus = (metric: Metric) => {
    const weeklyStatuses = weeks.map(week => {
       const goalRecord = metricGoals.find(
          (g) => g.metricId === metric.id && g.periodNumber === week.weekNumber && g.periodType === 'week'
        );
        
        const actual = goalRecord?.actualValue ?? null;
        const target = goalRecord?.goalValue ?? (metric.weeklyGoal || metric.quarterlyGoal || 0);

        return isMetricOnTrack(
          actual,
          target,
          metric.goalOrientation || 'gte',
          metric.goalRangeMin,
          metric.goalRangeMax
        ) ?? null;
    });

    const status = calculateTrailingStatus(weeklyStatuses, 3);
    
    if (status === 'green') return 'on-track';
    if (status === 'yellow') return 'at-risk';
    if (status === 'red') return 'off-track';
    return 'no-data';
  };



  const getOwnerName = (ownerId?: string) => {
    if (!ownerId) return 'Unassigned';
    const user = users.find((u) => u.id === ownerId);
    return user ? user.name : 'Unknown';
  };

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Sorted metrics (reuse logic or keep simple)
  const sortedMetrics = [...metrics].sort((a, b) => {
      // 1. Display Order
      if ((a.displayOrder ?? Infinity) !== (b.displayOrder ?? Infinity)) {
        return (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity);
      }
      return (a.metricName || a.measurable).localeCompare(b.metricName || b.measurable);
  });

  return (
    <Box border="1px" borderColor={borderColor} borderRadius="md" overflow="hidden">
      <Table variant="simple" size="md">
        <Thead bg={useColorModeValue('gray.50', 'gray.800')}>
          <Tr>
            <Th>Metric Name</Th>
            <Th>Owner</Th>
            <Th>Department</Th>
            <Th isNumeric>Goal (W{currentWeekNumber})</Th>
            <Th isNumeric>Actual</Th>
            <Th isNumeric>Variance</Th>
            <Th textAlign="center">Status</Th>
            <Th textAlign="right">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedMetrics.map((metric) => {
            // Find goal for current week
            // In a real app, you might query specific weekly goals. 
            // Here we assume metric.weeklyGoal is the standard, 
            // or find an override in metricGoals.
            // Simplified for this view:
            const target = metric.weeklyGoal ?? (metric.quarterlyGoal ? metric.quarterlyGoal : 0);
            
            // Find actual for current week from metricGoals history
            // Logic: find a MetricGoal record for this metric and this periodNumber?
            // The existing data structure separates 'Metric' definition from 'MetricGoal' (which seems to hold actuals?)
            // Let's look at `metricGoals`.
            const goalRecord = metricGoals.find(
              (g) => g.metricId === metric.id && g.periodNumber === currentWeekNumber && g.periodType === 'week'
            );
            
            const actual = goalRecord?.actualValue ?? null;
            const goalValue = goalRecord?.goalValue ?? target; // Check for override

            const status = getMetricStatus(metric);
            
            // Variance
            let variance: string | number = '-';
            if (actual !== null) {
               variance = actual - goalValue;
               // Format variance based on type? 
               // e.g. if percentage, 5 means 5% diff
            }

            return (
              <Tr key={metric.id} _hover={{ bg: hoverBg }}>
                <Td fontWeight="medium">
                  <Text>{metric.metricName}</Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>{metric.measurable}</Text>
                </Td>
                <Td>
                  <HStack spacing={2}>
                    {/* Could add Avatar here */}
                    <Text>{getOwnerName(metric.ownerId)}</Text>
                  </HStack>
                </Td>
                <Td>
                   <Badge variant="subtle" colorScheme="gray">
                    {metric.category || '-'}
                   </Badge>
                </Td>
                <Td isNumeric>
                  {metric.goalOrientation === 'range' && metric.goalRangeMin != null && metric.goalRangeMax != null ? (
                    `${formatMetricValue(metric.goalRangeMin, metric.measurementType, '')}-${formatMetricValue(metric.goalRangeMax, metric.measurementType, metric.unit)}`
                  ) : (
                    formatMetricValue(goalValue, metric.measurementType, metric.unit)
                  )}
                </Td>
                <Td isNumeric fontWeight="bold">
                   {actual !== null ? (
                      formatMetricValue(actual, metric.measurementType, metric.unit)
                   ) : (
                     <Text color="gray.400">-</Text>
                   )}
                </Td>
                <Td isNumeric color={status === 'off-track' ? 'red.500' : status === 'on-track' ? 'green.500' : 'gray.500'}>
                   {typeof variance === 'number' ? (variance > 0 ? '+' : '') + formatMetricValue(variance, metric.measurementType, '') : '-'}
                </Td>
                <Td textAlign="center">
                   {status === 'on-track' && (
                      <Badge colorScheme="green" variant="solid" borderRadius="full" px={2}>On Track</Badge>
                   )}
                   {status === 'off-track' && (
                      <Badge colorScheme="red" variant="solid" borderRadius="full" px={2}>Off Track</Badge>
                   )}
                   {status === 'on-track' && ( // Green
                      <Badge colorScheme="green" variant="solid" borderRadius="full" px={2}>On Track</Badge>
                   )}
                   {status === 'at-risk' && ( // Yellow
                      <Badge colorScheme="orange" variant="solid" borderRadius="full" px={2}>At Risk</Badge>
                   )}
                   {status === 'off-track' && ( // Red
                      <Badge colorScheme="red" variant="solid" borderRadius="full" px={2}>Off Track</Badge>
                   )}
                   {status === 'no-data' && (
                      <Badge colorScheme="gray" variant="outline" borderRadius="full" px={2}>No Data</Badge>
                   )}
                </Td>
                <Td textAlign="right">
                  <HStack justify="flex-end" spacing={1}>
                    <Tooltip label="Edit Metric">
                      <IconButton
                        aria-label="Edit metric"
                        icon={<Edit2 size={16} />}
                        size="sm"
                        variant="ghost"
                        onClick={() => onEdit(metric)}
                      />
                    </Tooltip>
                    <Tooltip label="Delete Metric">
                      <IconButton
                        aria-label="Delete metric"
                        icon={<Trash2 size={16} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => onDelete(metric)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            );
          })}
          {metrics.length === 0 && (
             <Tr>
               <Td colSpan={8} textAlign="center" py={8} color="gray.500">
                 No metrics found for this view.
               </Td>
             </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
}
